import { NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, inject, input } from '@angular/core';
import { Icon, type IconName } from '@tvd/design-system/icon';
import { ProgressSpinner } from '@tvd/design-system/progress-spinner';

export type ButtonKind =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'transparent'
  | 'danger'
  | 'inactive';

export type ButtonType = 'button' | 'submit' | 'reset';

@Component({
  selector: 'button[zynButton], a[zynButton]',
  imports: [NgTemplateOutlet, ProgressSpinner, Icon],
  host: {
    class: 'zyn-button-base inline-flex items-center h-9 sm:h-10 select-none',
    // A bare <button> defaults to type="submit"; default to "button" so it never
    // submits a surrounding form by accident. An explicit type="submit" in the
    // template binds straight to the `type` input and overrides this. Anchors
    // get a null default, so no bogus type attribute is rendered on them.
    '[attr.type]': 'type()',
    '[attr.kind]': 'kind()',
    '[attr.disabled]': 'disabled() || loading() ? true : null',
    '[class.pointer-events-none]': 'disabled() || loading()',
    '[class.cursor-not-allowed]': 'disabled() || loading()',
    '[attr.data-icon-name]': 'icon()',
    '[class.zyn-button-primary]': 'kind() === "primary"',
    '[class.zyn-button-secondary]': 'kind() === "secondary"',
    '[class.zyn-button-outline]': 'kind() === "outline"',
    '[class.zyn-button-transparent]': 'kind() === "transparent"',
    '[class.zyn-button-danger]': 'kind() === "danger"',
    '[class.zyn-button-inactive]': 'kind() === "inactive"',
    '[class.w-9]': 'icon() !== null && label() === null',
    '[class.sm:w-10]': 'icon() !== null && label() === null',
    '[class.gap-2]': 'icon() !== null && label() !== null',
    '[class.pl-3]': 'icon() === null && label()',
  },
  template: `
    <ng-template #iconFragment>
      <tvd-icon [icon]="icon()" class="!text-xl sm:!text-2xl" />
    </ng-template>
    @if (icon() && iconPosition() === 'left') {
      <ng-container *ngTemplateOutlet="iconFragment" />
    }
    <span class="zyn-button-label relative">
      @if (loading()) {
        <span class="invisible ">{{ label() }}</span>
        <tvd-progress-spinner
          size="sm"
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none"
          color="secondary"
        />
      } @else {
        {{ label() }}
      }
    </span>
    @if (icon() && iconPosition() === 'right') {
      <ng-container *ngTemplateOutlet="iconFragment" />
    }
  `,
})
export class Button {
  readonly kind = input<ButtonKind>('primary');
  readonly icon = input<IconName | null>(null);
  readonly label = input<string | null>(null);
  readonly disabled = input(false);
  readonly iconPosition = input<'left' | 'right'>('left');
  readonly loading = input(false);
  // Default 'button' on real <button> hosts; null on anchors (which take no type).
  readonly type = input<ButtonType | null>(
    (inject(ElementRef).nativeElement as HTMLElement).tagName === 'BUTTON'
      ? 'button'
      : null,
  );
}
